import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { ApiService, QueryParams } from './api/api.service';
import { buildResource } from './utils/api-resource.utils';
import {
  UploadType,
  InitiateUploadRequest,
  InitiateUploadResponse,
  GetPartUrlsRequest,
  GetPartUrlsResponse,
  CompleteUploadRequest,
  CompleteUploadResponse,
} from './model';

export interface UploadProgress {
  uploadId: string;
  filename: string;
  progress: number; // 0-100
  status: 'uploading' | 'paused' | 'completed' | 'failed' | 'cancelled';
  speed?: number; // bytes/sec (moving average)
  remainingTime?: number; // seconds
  error?: string;
}

export enum DocumentUploadTransactionsEnum {
  INIT_UPLOAD_DOCUMENT = 'init-document-upload',
  GET_PART_URLS = 'document-get-part-urls',
  COMPLETE_UPLOAD_DOCUMENT = 'complete-document-upload',
}

@Injectable({ providedIn: 'root' })
export class SecureDocumentUploadService {
  protected readonly resource = 'documents';
  private readonly defaultChunkSize = 5 * 1024 * 1024; // 5 MiB
  private readonly maxConcurrentUploads = 3;
  private readonly maxRetries = 3;

  private readonly uploadProgressSubjects = new Map<string, BehaviorSubject<UploadProgress>>();
  private readonly activeUploads = new Map<string, AbortController>();
  private readonly apiService: ApiService = inject(ApiService);

  uploadFile(file: File, documentTypeId?: string, metadata?: Record<string, unknown>): Observable<UploadProgress> {
    const correlationId = this.generateCorrelationId();

    const progressSubject = new BehaviorSubject<UploadProgress>({
      uploadId: '',
      filename: file.name,
      progress: 0,
      status: 'uploading',
      speed: 0,
      remainingTime: undefined,
    });

    // fire & forget – Fortschritt kommt über das Subject
    this.performSecureUpload(file, documentTypeId, metadata, correlationId, progressSubject).catch((error: unknown) => {
      const msg = (error as Error)?.message ?? 'Upload failed';
      progressSubject.next({
        ...progressSubject.value,
        status: 'failed',
        error: msg,
      });
      progressSubject.complete();
    });

    return progressSubject.asObservable();
  }

  cancelUpload(uploadId: string): void {
    const controller = this.activeUploads.get(uploadId);
    if (controller) {
      controller.abort();
      this.activeUploads.delete(uploadId);
    }

    const progressSubject = this.uploadProgressSubjects.get(uploadId);
    if (progressSubject) {
      progressSubject.next({ ...progressSubject.value, status: 'cancelled' });
      progressSubject.complete();
      this.uploadProgressSubjects.delete(uploadId);
    }
  }

  getUploadProgress(uploadId: string): Observable<UploadProgress> | null {
    return this.uploadProgressSubjects.get(uploadId)?.asObservable() ?? null;
  }

  getActiveUploads(): UploadProgress[] {
    return Array.from(this.uploadProgressSubjects.values()).map((s) => s.value);
  }

  // ---------------- core flow ----------------

  private async performSecureUpload(
    file: File,
    documentTypeId: string | undefined,
    metadata: Record<string, unknown> | undefined,
    correlationId: string,
    progressSubject: BehaviorSubject<UploadProgress>
  ): Promise<void> {
    const controller = new AbortController();
    let uploadId = '';
    try {
      // 1) Initiate
      const initRequest: InitiateUploadRequest = {
        filename: file.name,
        fileType: file.type || 'application/octet-stream',
        fileSize: file.size,
        documentTypeId,
        metadata,
        correlationId,
      };

      const initResponse = await this.initiateUpload(initRequest);
      uploadId = initResponse.uploadId;
      this.activeUploads.set(uploadId, controller);
      this.uploadProgressSubjects.set(uploadId, progressSubject);

      progressSubject.next({
        uploadId,
        filename: file.name,
        progress: 5, // Initiation done
        status: 'uploading',
        speed: 0,
      });

      let uploadParts: Array<{
        readonly partNumber: number;
        readonly etag: string;
      }> = [];

      // 2) Upload
      if (initResponse.uploadType === UploadType.SINGLE_PART) {
        await this.performSinglePartUpload(file, initResponse, progressSubject, controller.signal);
      } else {
        uploadParts = await this.performMultipartUpload(file, initResponse, progressSubject, controller.signal);
      }

      // 3) Complete
      const completeResponse = await this.completeUpload({ uploadId, parts: uploadParts });

      progressSubject.next({
        uploadId,
        filename: file.name,
        progress: 100,
        status: 'completed',
        speed: 0,
        remainingTime: 0,
      });

      // optional: hier könntest du documentId zurückmelden (z.B. in Subject state erweitern)
      void completeResponse;
      progressSubject.complete();
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return; // cancel
      throw error;
    } finally {
      this.activeUploads.delete(uploadId);
      // kleine Gnadenfrist, damit UI den Status noch lesen kann
      setTimeout(() => this.uploadProgressSubjects.delete(uploadId), 5000);
    }
  }

  // ---------------- single-part ----------------

  private async performSinglePartUpload(
    file: File,
    initResponse: InitiateUploadResponse,
    progressSubject: BehaviorSubject<UploadProgress>,
    signal: AbortSignal
  ): Promise<void> {
    if (!initResponse.presignedUrl) throw new Error('Presigned URL not provided for single part upload');

    const headers = new HttpHeaders({
      'Content-Type': file.type || 'application/octet-stream',
      ...(initResponse.uploadHeaders || {}),
    });

    const startTime = performance.now();
    const bytesTotal = file.size;

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const onAbort = () => {
        xhr.abort();
        cleanup();
        reject(Object.assign(new Error('Upload cancelled'), { name: 'AbortError' }));
      };

      const cleanup = () => {
        signal.removeEventListener('abort', onAbort);
        xhr.upload.removeEventListener('progress', onProgress);
        xhr.removeEventListener('load', onLoad);
        xhr.removeEventListener('error', onError);
      };

      const onProgress = (event: ProgressEvent) => {
        if (!event.lengthComputable) return;
        const elapsedSec = (performance.now() - startTime) / 1000;
        const speed = this.movingAverageSpeed(bytesTotal, event.loaded, elapsedSec);
        const pct = Math.min(95, Math.round((event.loaded / bytesTotal) * 90) + 5); // 5–95
        const remainingTime = speed > 0 ? Math.max(0, Math.round((bytesTotal - event.loaded) / speed)) : undefined;

        progressSubject.next({
          ...progressSubject.value,
          progress: pct,
          speed,
          remainingTime,
        });
      };

      const onLoad = () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status: ${xhr.status}`));
      };

      const onError = () => {
        cleanup();
        reject(new Error('Upload failed due to network error'));
      };

      xhr.upload.addEventListener('progress', onProgress);
      xhr.addEventListener('load', onLoad);
      xhr.addEventListener('error', onError);
      signal.addEventListener('abort', onAbort);

      xhr.open('PUT', initResponse.presignedUrl!);

      // korrekte Header-Iteration
      for (const key of headers.keys()) {
        if (key.toLowerCase() === 'content-length') continue;
        const val = headers.get(key);
        if (val != null) xhr.setRequestHeader(key, val);
      }

      xhr.send(file);
    });
  }

  // ---------------- multipart ----------------

  private async performMultipartUpload(
    file: File,
    initResponse: InitiateUploadResponse,
    progressSubject: BehaviorSubject<UploadProgress>,
    signal: AbortSignal
  ): Promise<Array<{ readonly partNumber: number; readonly etag: string }>> {
    const chunkSize = initResponse.chunkSize || this.defaultChunkSize;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const parts: Array<{ partNumber: number; etag: string }> = [];

    const bytesTotal = file.size;
    let bytesUploaded = 0;
    const startedAt = performance.now();

    for (let batchStart = 0; batchStart < totalChunks; batchStart += this.maxConcurrentUploads) {
      const batchEnd = Math.min(batchStart + this.maxConcurrentUploads, totalChunks);
      const partNumbers = Array.from({ length: batchEnd - batchStart }, (_, i) => batchStart + i + 1);

      // Hole presigned URLs für die ganze Batch (weniger API-Calls)
      const partUrls = await this.getPartUrls({ uploadId: initResponse.uploadId, partNumbers });
      const urlMap = new Map(partUrls.parts.map((p) => [p.partNumber, p.presignedUrl]));

      const batchPromises = partNumbers.map(async (partNumber) => {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        const url = urlMap.get(partNumber);
        if (!url) throw new Error(`No presigned URL for part ${partNumber}`);

        const etag = await this.uploadChunkWithRetry(chunk, url, signal);
        parts.push({ partNumber, etag });

        // Fortschritt auf Byte-Basis (präziser als auf Part-Basis)
        bytesUploaded += end - start;
        const elapsedSec = (performance.now() - startedAt) / 1000;
        const speed = this.movingAverageSpeed(bytesTotal, bytesUploaded, elapsedSec);
        const remainingTime = speed > 0 ? Math.max(0, Math.round((bytesTotal - bytesUploaded) / speed)) : undefined;
        const overallProgress = Math.min(95, Math.round((bytesUploaded / bytesTotal) * 90) + 5);

        progressSubject.next({
          ...progressSubject.value,
          progress: overallProgress,
          speed,
          remainingTime,
        });
      });

      await Promise.all(batchPromises);

      if (signal.aborted) throw Object.assign(new Error('Upload cancelled'), { name: 'AbortError' });
    }

    // Sortiert zurückgeben für Complete-Request
    return parts.sort((a, b) => a.partNumber - b.partNumber);
  }

  private async uploadChunkWithRetry(chunk: Blob, presignedUrl: string, signal: AbortSignal): Promise<string> {
    let lastErr: unknown;
    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        return await this.uploadChunkToS3(chunk, presignedUrl, signal);
      } catch (e) {
        lastErr = e;
        if (signal.aborted || attempt === this.maxRetries - 1) break;
        const backoffMs = this.exponentialBackoffWithJitter(attempt);
        await this.sleep(backoffMs);
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error('Failed to upload chunk');
  }

  private uploadChunkToS3(chunk: Blob, presignedUrl: string, signal: AbortSignal): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      const onAbort = () => {
        xhr.abort();
        cleanup();
        reject(Object.assign(new Error('Upload cancelled'), { name: 'AbortError' }));
      };

      const cleanup = () => {
        signal.removeEventListener('abort', onAbort);
        xhr.removeEventListener('load', onLoad);
        xhr.removeEventListener('error', onError);
      };

      const onLoad = () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('Etag') || xhr.getResponseHeader('etag');
          if (!etag) return reject(new Error('ETag not received from S3'));
          resolve(etag.replace(/"/g, ''));
        } else {
          reject(new Error(`Chunk upload failed with status: ${xhr.status}`));
        }
      };

      const onError = () => {
        cleanup();
        reject(new Error('Chunk upload failed due to network error'));
      };

      xhr.addEventListener('load', onLoad);
      xhr.addEventListener('error', onError);
      signal.addEventListener('abort', onAbort);

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.send(chunk);
    });
  }

  // ---------------- API helpers ----------------

  private initiateUpload(request: InitiateUploadRequest): Promise<InitiateUploadResponse> {
    const params: QueryParams = {
      action: 'init',
    };
    const resource = buildResource(params, this.resource);
    let headers = new HttpHeaders().set('X-Correlation-ID', request.correlationId || this.generateCorrelationId());

    return firstValueFrom(
      this.apiService.post<InitiateUploadResponse>(
        resource,
        request,
        DocumentUploadTransactionsEnum.INIT_UPLOAD_DOCUMENT,
        undefined,
        headers
      )
    );
  }

  private getPartUrls(request: GetPartUrlsRequest): Promise<GetPartUrlsResponse> {
    const params: QueryParams = {
      action: 'partUrls',
    };
    const resource = buildResource(params, this.resource);
    return firstValueFrom(
      this.apiService.post<GetPartUrlsResponse>(resource, request, DocumentUploadTransactionsEnum.GET_PART_URLS)
    );
  }

  private completeUpload(request: CompleteUploadRequest): Promise<CompleteUploadResponse> {
    const params: QueryParams = {
      action: 'complete',
    };
    const resource = buildResource(params, this.resource);
    return firstValueFrom(
      this.apiService.post<CompleteUploadResponse>(
        resource,
        request,
        DocumentUploadTransactionsEnum.COMPLETE_UPLOAD_DOCUMENT
      )
    );
  }

  // ---------------- utils ----------------

  private movingAverageSpeed(totalBytes: number, loadedBytes: number, elapsedSec: number): number {
    if (elapsedSec <= 0) return 0;
    // einfacher gleitender Durchschnitt auf Gesamtdatenbasis
    // (für lokale Glättung könnte man hier eine Fensterfunktion einbauen)
    return loadedBytes / elapsedSec;
  }

  private exponentialBackoffWithJitter(attempt: number, base = 500, cap = 8000): number {
    const exp = Math.min(cap, base * Math.pow(2, attempt));
    const jitter = Math.random() * (exp * 0.3); // ±30% Jitter
    return Math.round(exp * 0.85 + jitter);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

  private generateCorrelationId(): string {
    return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
